import { getSessionEmail, normalizeEmail } from "./userSession";

const isBrowser = typeof window !== "undefined";
const EXPORT_FILES_LIMIT = 300;
const EXPORT_TAGS_LIMIT = 100;
const EXPORT_FOLDERS_LIMIT = 80;
const EXPORT_FILES_EVENT = "optra:export-files-change";
const EXPORT_OWNERS_KEY = "optra_export_known_owners_v1";
const DEV_SAMPLE_FILE_IDS = new Set([
  "file_dev_sample_csv",
  "file_dev_sample_docx",
  "file_dev_sample_pdf",
]);

const nowIso = () => new Date().toISOString();

const resolveOwnerEmail = (email) => normalizeEmail(email || getSessionEmail() || null);

const getFilesStorageKey = (email) => {
  const normalized = normalizeEmail(email);
  return normalized ? `optra_export_files_${normalized}` : "optra_export_files";
};

const getTagsStorageKey = (email) => {
  const normalized = normalizeEmail(email);
  return normalized ? `optra_export_tags_${normalized}` : "optra_export_tags";
};

const getFoldersStorageKey = (email) => {
  const normalized = normalizeEmail(email);
  return normalized ? `optra_export_folders_${normalized}` : "optra_export_folders";
};

const dispatchFilesChanged = () => {
  if (!isBrowser) return;
  window.dispatchEvent(new CustomEvent(EXPORT_FILES_EVENT));
};

const rememberOwnerEmail = (email) => {
  if (!isBrowser) return;
  const normalized = normalizeEmail(email);
  if (!normalized) return;
  try {
    const parsed = JSON.parse(window.localStorage.getItem(EXPORT_OWNERS_KEY) || "[]");
    const rows = Array.isArray(parsed) ? parsed : [];
    if (rows.includes(normalized)) return;
    window.localStorage.setItem(EXPORT_OWNERS_KEY, JSON.stringify([normalized, ...rows].slice(0, 200)));
  } catch {
    window.localStorage.setItem(EXPORT_OWNERS_KEY, JSON.stringify([normalized]));
  }
};

const readJson = (key, fallback = []) => {
  if (!isBrowser) return fallback;
  try {
    return JSON.parse(window.localStorage.getItem(key) || JSON.stringify(fallback));
  } catch {
    return fallback;
  }
};

const readRaw = (email) => readJson(getFilesStorageKey(email), []);
const readTagsRaw = (email) => readJson(getTagsStorageKey(email), []);
const readFoldersRaw = (email) => readJson(getFoldersStorageKey(email), []);

const writeRaw = (email, rows) => {
  if (!isBrowser) return;
  const key = getFilesStorageKey(email);
  rememberOwnerEmail(email);
  const sanitizeRows = (inputRows) =>
    inputRows.map((row) => {
      const next = { ...row };
      const downloadUrl = String(next.download_url || "");
      if (downloadUrl.startsWith("data:")) {
        next.download_url = "";
      }
      if (typeof next.content === "string" && next.content.length > 10000) {
        next.content = "";
      }
      return next;
    });
  const capped = rows.slice(0, EXPORT_FILES_LIMIT);
  try {
    window.localStorage.setItem(key, JSON.stringify(capped));
  } catch {
    // Quota safety: strip heavy fields then retry, then trim oldest rows if needed.
    let fallback = sanitizeRows(capped);
    try {
      window.localStorage.setItem(key, JSON.stringify(fallback));
      return;
    } catch {
      while (fallback.length > 0) {
        fallback = fallback.slice(0, Math.max(0, fallback.length - 25));
        try {
          window.localStorage.setItem(key, JSON.stringify(fallback));
          return;
        } catch {
          // continue trimming
        }
      }
    }
  }
};

const writeTagsRaw = (email, rows) => {
  if (!isBrowser) return;
  rememberOwnerEmail(email);
  window.localStorage.setItem(getTagsStorageKey(email), JSON.stringify(rows.slice(0, EXPORT_TAGS_LIMIT)));
};

const writeFoldersRaw = (email, rows) => {
  if (!isBrowser) return;
  rememberOwnerEmail(email);
  window.localStorage.setItem(getFoldersStorageKey(email), JSON.stringify(rows.slice(0, EXPORT_FOLDERS_LIMIT)));
};

export function subscribeToExportFiles(callback) {
  if (!isBrowser || typeof callback !== "function") return () => {};
  const handler = () => callback();
  window.addEventListener(EXPORT_FILES_EVENT, handler);
  window.addEventListener("storage", handler);
  return () => {
    window.removeEventListener(EXPORT_FILES_EVENT, handler);
    window.removeEventListener("storage", handler);
  };
}

export function listExportFiles(email) {
  const resolved = resolveOwnerEmail(email);
  const rows = readRaw(resolved);
  return rows.sort((a, b) => Date.parse(b.created_at || "") - Date.parse(a.created_at || ""));
}

export function listExportTags(email) {
  const resolved = resolveOwnerEmail(email);
  return readTagsRaw(resolved)
    .map((x) => String(x || "").trim())
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));
}

export function listExportFolders(email) {
  const resolved = resolveOwnerEmail(email);
  const rows = readFoldersRaw(resolved);
  return rows.sort((a, b) => Date.parse(b.created_at || "") - Date.parse(a.created_at || ""));
}

export function listKnownExportOwners() {
  if (!isBrowser) return [];
  try {
    const parsed = JSON.parse(window.localStorage.getItem(EXPORT_OWNERS_KEY) || "[]");
    return (Array.isArray(parsed) ? parsed : [])
      .map((email) => normalizeEmail(email || ""))
      .filter(Boolean);
  } catch {
    return [];
  }
}

export function ensureExportFolder(name, email, options = {}) {
  const resolved = resolveOwnerEmail(email);
  const trimmed = String(name || "").trim();
  if (!trimmed) return null;
  const normalizedName = trimmed.slice(0, 80);
  const rows = readFoldersRaw(resolved);
  const existing = rows.find(
    (row) => String(row?.name || "").trim().toLowerCase() === normalizedName.toLowerCase()
  );
  if (existing) return existing;
  const created = {
    id: `fld_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    owner_email: resolved,
    name: normalizedName,
    created_at: nowIso(),
    is_system: Boolean(options?.isSystem),
  };
  writeFoldersRaw(resolved, [created, ...rows]);
  dispatchFilesChanged();
  return created;
}

export function recordExportFile({
  email,
  file_name,
  file_type,
  source_tool,
  title,
  description,
  size_bytes,
  tags,
  folder_id,
  content,
  download_url,
} = {}) {
  const resolved = resolveOwnerEmail(email);
  if (!resolved || !file_name) return null;

  const entry = {
    id: `file_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    owner_email: resolved,
    file_name: String(file_name),
    file_type: String(file_type || "").toLowerCase(),
    source_tool: String(source_tool || "unknown"),
    title: String(title || file_name),
    description: String(description || ""),
    size_bytes: Number.isFinite(Number(size_bytes)) ? Number(size_bytes) : null,
    tags: Array.isArray(tags) ? tags.map((t) => String(t || "").trim()).filter(Boolean) : [],
    folder_id: folder_id ? String(folder_id).trim() : null,
    content: typeof content === "string" ? content : "",
    download_url: typeof download_url === "string" ? download_url : "",
    created_at: nowIso(),
  };

  const existing = readRaw(resolved);
  const merged = [entry, ...existing.filter((row) => row.id !== entry.id)];
  writeRaw(resolved, merged);
  dispatchFilesChanged();
  return entry;
}

export function setExportFileTags(fileId, tags, email) {
  const resolved = resolveOwnerEmail(email);
  if (!fileId) return null;
  const nextTags = Array.isArray(tags) ? tags.map((t) => String(t || "").trim()).filter(Boolean) : [];
  const rows = readRaw(resolved);
  let updated = null;
  const next = rows.map((row) => {
    if (row.id !== fileId) return row;
    updated = { ...row, tags: nextTags };
    return updated;
  });
  writeRaw(resolved, next);
  dispatchFilesChanged();
  return updated;
}

export function addExportTag(tag, email) {
  const resolved = resolveOwnerEmail(email);
  const normalizedTag = String(tag || "").trim();
  if (!normalizedTag) return null;
  const rows = readTagsRaw(resolved);
  const exists = rows.some((x) => String(x || "").trim().toLowerCase() === normalizedTag.toLowerCase());
  if (exists) return normalizedTag;
  writeTagsRaw(resolved, [normalizedTag, ...rows]);
  dispatchFilesChanged();
  return normalizedTag;
}

export function setExportFileFolder(fileId, folderId, email) {
  const resolved = resolveOwnerEmail(email);
  if (!fileId) return null;
  const normalizedFolderId = folderId ? String(folderId).trim() : null;
  const rows = readRaw(resolved);
  let updated = null;
  const next = rows.map((row) => {
    if (row.id !== fileId) return row;
    updated = { ...row, folder_id: normalizedFolderId || null };
    return updated;
  });
  writeRaw(resolved, next);
  dispatchFilesChanged();
  return updated;
}

export function renameExportFile(fileId, nextFileName, email) {
  const resolved = resolveOwnerEmail(email);
  const normalizedId = String(fileId || "").trim();
  const normalizedName = String(nextFileName || "").trim();
  if (!normalizedId || !normalizedName) return null;
  const rows = readRaw(resolved);
  let updated = null;
  const nextRows = rows.map((row) => {
    if (String(row?.id || "") !== normalizedId) return row;
    updated = { ...row, file_name: normalizedName, title: normalizedName };
    return updated;
  });
  if (!updated) return null;
  writeRaw(resolved, nextRows);
  dispatchFilesChanged();
  return updated;
}

export function removeExportFile(fileId, email) {
  const resolved = resolveOwnerEmail(email);
  const normalizedId = String(fileId || "").trim();
  if (!normalizedId) return false;
  const rows = readRaw(resolved);
  const nextRows = rows.filter((row) => String(row?.id || "") !== normalizedId);
  if (nextRows.length === rows.length) return false;
  const removed = rows.find((row) => String(row?.id || "") === normalizedId);
  const objectUrl = String(removed?.download_url || "");
  if (objectUrl.startsWith("blob:")) {
    try {
      URL.revokeObjectURL(objectUrl);
    } catch {
      // ignore
    }
  }
  writeRaw(resolved, nextRows);
  dispatchFilesChanged();
  return true;
}

export function createExportFolder(name, email) {
  const resolved = resolveOwnerEmail(email);
  const trimmed = String(name || "").trim();
  if (!trimmed) return null;
  const normalizedName = trimmed.slice(0, 80);
  const rows = readFoldersRaw(resolved);
  const exists = rows.some((row) => String(row?.name || "").trim().toLowerCase() === normalizedName.toLowerCase());
  if (exists) return null;
  return ensureExportFolder(normalizedName, resolved);
}

export function deleteExportFolder(folderId, email) {
  const resolved = resolveOwnerEmail(email);
  const normalizedFolderId = String(folderId || "").trim();
  if (!normalizedFolderId) return false;

  const folders = readFoldersRaw(resolved);
  const nextFolders = folders.filter((row) => String(row?.id || "") !== normalizedFolderId);
  if (nextFolders.length === folders.length) return false;
  writeFoldersRaw(resolved, nextFolders);

  const files = readRaw(resolved);
  const nextFiles = files.map((row) =>
    String(row?.folder_id || "") === normalizedFolderId ? { ...row, folder_id: null } : row
  );
  writeRaw(resolved, nextFiles);

  dispatchFilesChanged();
  return true;
}

export function seedDevSampleExportFile(email) {
  if (!isBrowser) return;
  const isDev = typeof import.meta !== "undefined" && import.meta.env && import.meta.env.DEV;
  if (!isDev) return;
  const resolved = resolveOwnerEmail(email) || null;
  const rows = readRaw(resolved);
  const hasType = (fileType) => rows.some((row) => String(row?.file_type || "").toLowerCase() === fileType);
  const samplesToAdd = [];

  if (!hasType("csv")) {
    samplesToAdd.push({
      id: "file_dev_sample_csv",
      owner_email: resolved || "dev@optra.local",
      file_name: "dummy_file.csv",
      file_type: "csv",
      source_tool: "vendor_intelligence",
      title: "Dummy CSV Export",
      description: "Dev sample file",
      size_bytes: 1536,
      tags: ["to-review"],
      folder_id: null,
      created_at: nowIso(),
    });
  }

  if (!hasType("docx")) {
    samplesToAdd.push({
      id: "file_dev_sample_docx",
      owner_email: resolved || "dev@optra.local",
      file_name: "dummy_document.docx",
      file_type: "docx",
      source_tool: "application_generator",
      title: "Dummy DOCX Export",
      description: "Dev sample file",
      size_bytes: 24576,
      tags: ["draft"],
      folder_id: null,
      created_at: nowIso(),
    });
  }

  if (!hasType("pdf")) {
    samplesToAdd.push({
      id: "file_dev_sample_pdf",
      owner_email: resolved || "dev@optra.local",
      file_name: "dummy_report.pdf",
      file_type: "pdf",
      source_tool: "grant_compliance_review",
      title: "Dummy PDF Export",
      description: "Dev sample file",
      size_bytes: 32768,
      tags: ["ready"],
      folder_id: null,
      created_at: nowIso(),
    });
  }

  if (!samplesToAdd.length) return;
  const next = [...samplesToAdd, ...rows.filter((row) => !samplesToAdd.some((sample) => sample.id === row.id))];
  writeRaw(resolved, next);
  dispatchFilesChanged();
}

export function removeDevSampleExportFiles(email) {
  if (!isBrowser) return false;
  const resolved = resolveOwnerEmail(email) || null;
  const rows = readRaw(resolved);
  const next = rows.filter((row) => !DEV_SAMPLE_FILE_IDS.has(String(row?.id || "")));
  if (next.length === rows.length) return false;
  writeRaw(resolved, next);
  dispatchFilesChanged();
  return true;
}

export function downloadExportFile(file) {
  if (!isBrowser || !file) return false;

  const url = file.download_url || file.url || null;
  if (url) {
    try {
      window.open(url, "_blank", "noopener,noreferrer");
      return true;
    } catch {
      return false;
    }
  }

  const content = String(file.content || "");
  if (!content) return false;

  try {
    const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = file.file_name || "optra-file.txt";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(blobUrl);
    return true;
  } catch {
    return false;
  }
}

const CRC32_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i += 1) {
    let c = i;
    for (let k = 0; k < 8; k += 1) {
      c = (c & 1) ? (0xedb88320 ^ (c >>> 1)) : (c >>> 1);
    }
    table[i] = c >>> 0;
  }
  return table;
})();

const crc32 = (bytes) => {
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i += 1) {
    crc = CRC32_TABLE[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
};

const sanitizeZipFileName = (name, fallback = "file.txt") => {
  const cleaned = String(name || "")
    .replace(/[\\/:*?"<>|]+/g, "_")
    .replace(/\s+/g, " ")
    .trim();
  return cleaned || fallback;
};

const toUint8 = async (item) => {
  const source = item || {};
  const fromBlob = source.blob instanceof Blob ? source.blob : null;
  if (fromBlob) return new Uint8Array(await fromBlob.arrayBuffer());
  const url = source.download_url || source.url || "";
  if (url && typeof fetch === "function") {
    try {
      const response = await fetch(url);
      if (response.ok) {
        return new Uint8Array(await response.arrayBuffer());
      }
    } catch {
      // ignore and continue with fallback
    }
  }
  const content = String(source.content || "");
  return new TextEncoder().encode(content);
};

const appendUint16 = (arr, value) => {
  arr.push(value & 0xff, (value >>> 8) & 0xff);
};

const appendUint32 = (arr, value) => {
  arr.push(value & 0xff, (value >>> 8) & 0xff, (value >>> 16) & 0xff, (value >>> 24) & 0xff);
};

export async function downloadFilesAsZip(files, zipName = "optra-files.zip") {
  if (!isBrowser || !Array.isArray(files) || files.length === 0) return false;
  const normalizedZipName = sanitizeZipFileName(zipName, "optra-files.zip").endsWith(".zip")
    ? sanitizeZipFileName(zipName, "optra-files.zip")
    : `${sanitizeZipFileName(zipName, "optra-files.zip")}.zip`;
  try {
    const entries = [];
    for (const file of files) {
      const bytes = await toUint8(file);
      const name = sanitizeZipFileName(file?.file_name || file?.title, `file-${entries.length + 1}.txt`);
      const nameBytes = new TextEncoder().encode(name);
      entries.push({ nameBytes, bytes, crc: crc32(bytes) });
    }

    const chunks = [];
    const central = [];
    let offset = 0;

    for (const entry of entries) {
      const localHeader = [];
      appendUint32(localHeader, 0x04034b50);
      appendUint16(localHeader, 20);
      appendUint16(localHeader, 0);
      appendUint16(localHeader, 0);
      appendUint16(localHeader, 0);
      appendUint16(localHeader, 0);
      appendUint32(localHeader, entry.crc);
      appendUint32(localHeader, entry.bytes.length);
      appendUint32(localHeader, entry.bytes.length);
      appendUint16(localHeader, entry.nameBytes.length);
      appendUint16(localHeader, 0);
      chunks.push(new Uint8Array(localHeader), entry.nameBytes, entry.bytes);

      const centralHeader = [];
      appendUint32(centralHeader, 0x02014b50);
      appendUint16(centralHeader, 20);
      appendUint16(centralHeader, 20);
      appendUint16(centralHeader, 0);
      appendUint16(centralHeader, 0);
      appendUint16(centralHeader, 0);
      appendUint16(centralHeader, 0);
      appendUint32(centralHeader, entry.crc);
      appendUint32(centralHeader, entry.bytes.length);
      appendUint32(centralHeader, entry.bytes.length);
      appendUint16(centralHeader, entry.nameBytes.length);
      appendUint16(centralHeader, 0);
      appendUint16(centralHeader, 0);
      appendUint16(centralHeader, 0);
      appendUint16(centralHeader, 0);
      appendUint32(centralHeader, 0);
      appendUint32(centralHeader, offset);
      central.push(new Uint8Array(centralHeader), entry.nameBytes);

      offset += localHeader.length + entry.nameBytes.length + entry.bytes.length;
    }

    const centralSize = central.reduce((sum, chunk) => sum + chunk.length, 0);
    const endHeader = [];
    appendUint32(endHeader, 0x06054b50);
    appendUint16(endHeader, 0);
    appendUint16(endHeader, 0);
    appendUint16(endHeader, entries.length);
    appendUint16(endHeader, entries.length);
    appendUint32(endHeader, centralSize);
    appendUint32(endHeader, offset);
    appendUint16(endHeader, 0);

    const blob = new Blob([...chunks, ...central, new Uint8Array(endHeader)], { type: "application/zip" });
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = normalizedZipName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(blobUrl);
    return true;
  } catch {
    return false;
  }
}
